import React from "react";
import { Breadcrumbs, StepIndicator, Container } from "@telia/teddy";
import classnames from "classnames";
import { connect, useSelector } from "react-redux";
import { useLocation, useParams, Outlet } from "react-router-dom";
import { TELIA_PLAY_LABELS, TELIA_PLAY_LINKS } from "../../../../../../constants";
import PAGES from "../../../../../../constants/pages";
import { RootState } from "../../../../../../models";
import { getSelectedPackageId } from "../../../state/selectors";
import { getPackageSlugFromId } from "../../../../../../shared/teliaPlayPackageMapping";
import { ConnectedAlert, ProgressBar } from "../../../../components";
import { selectors as LOADING_SELECTORS } from "../../../../loading";
import ContinueSection from "../../molecules/ContinueSection/ContinueSection";
import "./TeliaPlayPage.less";

function locationToTeliaPlayLinkIndex(path: string): number {
    const links = TELIA_PLAY_LINKS as readonly string[];
    const exactIndex = links.indexOf(path);
    if (exactIndex >= 0) return exactIndex;

    let matchedIndex = -1;
    let matchedLength = -1;

    links.forEach((link, index) => {
        if (path.startsWith(link) && link.length > matchedLength) {
            matchedIndex = index;
            matchedLength = link.length;
        }
    });

    return matchedIndex;
}

function locationToStepIndicatorIndex(path: string) {
    const index = locationToTeliaPlayLinkIndex(path);
    return Math.max(index, 0);
}

function locationToLabel(path: string): string {
    if (path === PAGES.TELIA_PLAY.CUSTOMER.URL) return "Kasse";
    const index = locationToTeliaPlayLinkIndex(path);
    return TELIA_PLAY_LABELS[index] ?? "";
}

export function getTeliaPlayStepLinks(packageSlug?: string): string[] {
    return TELIA_PLAY_LINKS.map((link, index) =>
        index === 0 && packageSlug ? `${link}${encodeURIComponent(packageSlug)}/` : link,
    );
}

interface TeliaPlayPageProps {
    className?: string;
    isFetchingData: boolean;
}

const TeliaPlayPage: React.FC<TeliaPlayPageProps> = ({ className = "", isFetchingData }) => {
    const location = useLocation();
    const { packageId: routePackageSlug } = useParams();
    const currentPackageSlug = useSelector(
        (state: RootState) =>
            getPackageSlugFromId(getSelectedPackageId(state) ?? state.teliaPlay?.requestedPackageId) ??
            routePackageSlug,
    );
    const stepLinks = getTeliaPlayStepLinks(currentPackageSlug);
    const currentLabel = locationToLabel(location.pathname);
    const isReceiptPage = location.pathname.startsWith(PAGES.TELIA_PLAY.RECEIPT.URL);
    return (
        <div
            className={classnames("telia-play-page", {
                [className]: className,
            })}
        >
            {!isReceiptPage && (
                <>
                    <Container className="telia-play-page__container">
                        <div className="telia-play-page__breadcrumbs">
                            <Breadcrumbs>
                                <Breadcrumbs.List>
                                    <Breadcrumbs.Item>
                                        <Breadcrumbs.Link href="/" home>
                                            Forsiden
                                        </Breadcrumbs.Link>
                                    </Breadcrumbs.Item>
                                    <Breadcrumbs.Separator />
                                    <Breadcrumbs.Item>
                                        <Breadcrumbs.Link href={"https://www.teliaplay.no"}>
                                            Telia Play
                                        </Breadcrumbs.Link>
                                    </Breadcrumbs.Item>
                                    <Breadcrumbs.Separator />
                                    <Breadcrumbs.Item>
                                        <Breadcrumbs.Page>{currentLabel}</Breadcrumbs.Page>
                                    </Breadcrumbs.Item>
                                </Breadcrumbs.List>
                            </Breadcrumbs>
                        </div>
                    </Container>
                    <div className="telia-play-page__step-indicator">
                        <StepIndicator
                            index={locationToStepIndicatorIndex(location.pathname)}
                            numberOfSteps={3}
                            labels={TELIA_PLAY_LABELS}
                            links={stepLinks}
                            kind="purple"
                        />
                    </div>
                </>
            )}
            <ProgressBar
                isFetchingData={!location.pathname.includes("/kasse/verifisering/") && isFetchingData}
                backgroundColor="white"
            />
            <ConnectedAlert />
            <div className="telia-play-page__content">
                <Outlet />
                {!isReceiptPage && <ContinueSection path={location.pathname} />}
            </div>
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    isFetchingData: LOADING_SELECTORS.isLoading(state),
});

export default connect(mapStateToProps)(TeliaPlayPage);
